import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, IconButton,
  Avatar, Box, Container, Tooltip, BottomNavigation,
  BottomNavigationAction, Paper, Alert, Button
} from '@mui/material';
import {
  Logout as LogoutIcon,
  CalendarMonth as ScheduleIcon,
  Person as PersonIcon,
  AddTask as AvailableIcon,
  Paid as EarningsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getViewAsSession, exitViewAsSession } from '../../utils/viewAsSession';

const MaidPortalLayout = ({ children, activeView = 'schedule', onViewChange }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '?';

  // An admin viewing this portal as the technician (read-only, this tab only)
  const viewAs = getViewAsSession();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', pb: 8 }}>
      {viewAs && (
        <Alert
          severity="warning"
          variant="filled"
          square
          sx={{ position: 'sticky', top: 0, zIndex: (theme) => theme.zIndex.appBar + 1 }}
          action={(
            <Button color="inherit" size="small" variant="outlined" onClick={exitViewAsSession}>
              Exit
            </Button>
          )}
        >
          Viewing as <strong>{viewAs.name}</strong> — read-only: anything you try to change will be refused.
        </Alert>
      )}
      <AppBar position="static" color="primary" elevation={1}>
        <Toolbar>
          <Box component="img" src="/images/logo2.jpg" alt="PrimeShine" sx={{ height: 36, mr: 2, borderRadius: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {activeView === 'profile'
              ? 'My Profile'
              : activeView === 'available'
                ? 'Available Jobs'
                : activeView === 'earnings'
                  ? 'My Earnings'
                  : 'My Schedule'}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', color: 'secondary.contrastText', fontSize: 13 }}>
              {initials}
            </Avatar>
            <Typography variant="body2" sx={{ color: 'primary.contrastText', display: { xs: 'none', sm: 'block' } }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Tooltip title="Sign out">
              <IconButton color="inherit" onClick={() => logout(navigate)} size="small" sx={{ ml: 1 }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth={activeView === 'schedule' ? 'lg' : 'sm'} sx={{ py: 3 }}>
        {children}
      </Container>

      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10 }} elevation={3}>
        <BottomNavigation value={activeView} onChange={(_, v) => onViewChange?.(v)} showLabels>
          <BottomNavigationAction label="Schedule" value="schedule" icon={<ScheduleIcon />} />
          <BottomNavigationAction label="Available" value="available" icon={<AvailableIcon />} />
          <BottomNavigationAction label="Earnings" value="earnings" icon={<EarningsIcon />} />
          <BottomNavigationAction label="My Profile" value="profile" icon={<PersonIcon />} />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default MaidPortalLayout;
